/**
 * CSRF token accessor + global fetch guard (UNIT-77, hardened UNIT-125).
 *
 * The unified host protects every mutating request with a session-synchroniser token
 * (apps/web/src/middleware/csrf.ts) read from the `X-CSRF-Token` header. The opa shell
 * publishes that token in `<meta name="csrf-token">`; this reads it so network.ts (the
 * single AJAX choke point) and any direct `fetch()` can send it. TradeVision's old
 * `Authorization: Bearer <jwt>` scheme is gone — identity is the session cookie.
 *
 * network.ts was never the only choke point: the lifted TV code also calls `fetch()`
 * directly in ~9 places, and those all 403'd after cutover (trade-journal save/upload,
 * tag writes, AI chat). Every site now passes the header explicitly AND
 * `installCsrfFetchGuard()` back-stops the next one someone forgets. The guard is
 * strictly same-origin: the token must never ride to Google/Stripe/etc.
 */

const HEADER = "X-CSRF-Token";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** Fired when a request failed CSRF *and* the session is gone — the user must sign in again. */
export const SESSION_EXPIRED_EVENT = "tv:session-expired";

export function csrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute("content") ?? "";
}

/** Write a refreshed token back to the shell's meta tag so later requests pick it up. */
function setCsrfToken(token: string): void {
  let meta = document.querySelector('meta[name="csrf-token"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "csrf-token");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", token);
  // HTMX reads its token from hx-headers on <body>, which was stamped at render time; keep the
  // two in step or HTMX-driven pages keep failing after the fetch path has recovered.
  const body = document.body;
  if (body?.hasAttribute("hx-headers")) {
    body.setAttribute("hx-headers", JSON.stringify({ "X-CSRF-Token": token }));
  }
}

/**
 * Re-read the session's current token from the host.
 *
 * Returns `authenticated: false` when the session is gone entirely, which is the signal to stop
 * retrying and ask the user to sign in rather than silently failing forever.
 */
export async function refreshCsrfToken(
  // Callers pass the UNWRAPPED fetch so the refresh cannot recurse back through the guard.
  fetchImpl: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> = fetch,
): Promise<{ token: string; authenticated: boolean }> {
  try {
    const res = await fetchImpl("/api/csrf-token", {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return { token: "", authenticated: false };
    const data = (await res.json()) as { token?: string; authenticated?: boolean };
    const token = typeof data.token === "string" ? data.token : "";
    if (token) setCsrfToken(token);
    return { token, authenticated: Boolean(data.authenticated) };
  } catch {
    // Offline or the host is down — indistinguishable from here, and neither is a session
    // problem. Report unauthenticated=false so we surface a message instead of looping.
    return { token: "", authenticated: false };
  }
}

/** Merge the CSRF header into a headers object (used by network.ts + direct fetch sites). */
export function withCsrf(headers: Record<string, string> = {}): Record<string, string> {
  return { ...headers, [HEADER]: csrfToken() };
}

/**
 * Would the host's csrf() demand a token for this request?
 *
 * True only for same-origin mutating requests. A cross-origin or unparseable URL is
 * false so the guard never leaks the session's token off-site.
 */
export function needsCsrf(method: string, url: string, origin: string): boolean {
  if (SAFE_METHODS.has(method.toUpperCase())) return false;
  try {
    return new URL(url, origin).origin === origin;
  } catch {
    return false;
  }
}

interface GuardedFetch {
  (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
  __csrfGuarded?: true;
}

/**
 * Can this request be replayed after a token refresh?
 *
 * A one-shot stream body cannot be re-sent, so retrying it would silently post nothing. Every
 * body the app actually uses (JSON strings, FormData) is replayable.
 */
function isReplayable(body: BodyInit | null | undefined): boolean {
  if (body == null) return true;
  return (
    typeof body === "string" ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body)
  );
}

/** A 403 specifically from the CSRF middleware, not from a tier/feature gate. */
function isCsrfFailure(res: Response): boolean {
  return res.status === 403 && res.headers.get("X-CSRF-Failed") === "1";
}

/**
 * Wrap `window.fetch` so same-origin mutating requests always carry the token.
 *
 * Idempotent, and never overwrites a header the caller set itself. Installed at bundle
 * load (index.ts) so it is in place before any page code runs.
 */
export function installCsrfFetchGuard(w: Window & typeof globalThis = window): void {
  const original = w.fetch as GuardedFetch | undefined;
  if (!original || original.__csrfGuarded) return;

  const guarded: GuardedFetch = async (input, init) => {
    const request = input instanceof Request ? input : null;
    const url = request ? request.url : String(input);
    const method = init?.method ?? request?.method ?? "GET";

    if (!needsCsrf(method, url, w.location.origin)) return original.call(w, input, init);

    const token = csrfToken();

    if (request) {
      // A Request object's body may already be a consumed stream, so this path only stamps the
      // header — recovery below is for the (url, init) form every caller in this bundle uses.
      if (!token || request.headers.has(HEADER)) return original.call(w, input, init);
      const headers = new Headers(request.headers);
      headers.set(HEADER, token);
      return original.call(w, new Request(request, { headers }), init);
    }

    // Build from init.headers so a FormData body keeps its auto-generated
    // multipart Content-Type (we only ever add our own header).
    const headers = new Headers(init?.headers ?? undefined);
    const callerSetToken = headers.has(HEADER);
    if (!callerSetToken && token) headers.set(HEADER, token);

    const res = await original.call(w, input, { ...init, headers });

    // --- stale-token recovery ------------------------------------------------------------
    // A tab open across a session rotation holds the token its shell was rendered with, so
    // every save 403s until a reload — which throws away whatever the user had typed. Re-read
    // the current token and replay the request once, invisibly.
    if (!isCsrfFailure(res) || !isReplayable(init?.body)) return res;

    const refreshed = await refreshCsrfToken((i, ini) => original.call(w, i, ini));
    if (!refreshed.authenticated || !refreshed.token) {
      // Genuinely signed out: no token will help. Tell the app so it can ask the user to sign
      // in WITHOUT discarding their work, and hand back the original failure.
      w.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { url } }));
      return res;
    }
    if (refreshed.token === token) return res; // token was already current — a real rejection.

    // A fresh Headers for the replay: mutating the one the first attempt used would retroactively
    // rewrite that request's headers for anything still holding a reference to them.
    const retryHeaders = new Headers(headers);
    retryHeaders.set(HEADER, refreshed.token);
    return original.call(w, input, { ...init, headers: retryHeaders });
  };

  guarded.__csrfGuarded = true;
  w.fetch = guarded;
}
